import React from 'react';
import * as S from './styles';
import { ButtonBaseProps } from './types';

// The only reason to have a component that wraps UnstyledButton rather than just
// exporting S.Button as ButtonBase is to standardize the `as` prop.
// If a styled-component were exported as `ButtonBase`, then people would have to
// know to pass `forwardedAs` rather than `as` when changing the element
// that gets rendered.
function ForwardedButtonBase<
  T extends Element = HTMLButtonElement,
  As extends React.ElementType = 'button',
>({ as, ...props }: ButtonBaseProps<As>, forwardedRef: React.ForwardedRef<T>) {
  const Component = as ?? 'button';

  return <S.Button {...props} forwardedAs={Component} ref={forwardedRef} />;
}

/**
 * An minimally-styled button used to build other clickable components.
 *
 *
 * `ButtonBase` is accessible and designed to work across multiple devices.  Styling
 * is more reliable when using data properties applied to the button rather than
 * CSS pseudo classes.
 *
 * For example, when hovered, the `data-hovered="true"` property is added to the
 * button.  Similarly, when the button is pressed, `data-pressed="true"` is applied.
 *
 * For a description of the button's accessibility props, see <a href="https://react-spectrum.adobe.com/react-aria/useButton.html" target="_blank">useButton</a>
 */
export const ButtonBase = React.forwardRef(ForwardedButtonBase) as <
  T extends Element = HTMLButtonElement,
  As extends React.ElementType = 'button',
>(
  props: ButtonBaseProps<As> & { ref?: React.Ref<T> },
) => React.ReactElement | null;

export default ButtonBase;
